import { computed, ref, watch, type Ref } from 'vue'
import { notLoadCookieBanner } from '../utils/oneTrustLocales'

declare global {
  interface Window {
    OnetrustActiveGroups: string | undefined
  }
}

export type UserCookieConsentOptions = {
  /** OneTrust category ids that must ALL be active, e.g. ['C0002'] */
  categories: string[]
  /** `xx-yy`. Pass it from middleware (`to.params.locale`); defaults to the current route. */
  currentCountryLocale?: string
  /** Fires once, the first time consent is true (immediately if already true at setup). */
  onFirstAccept?: () => void
  /** Fires on every change of consent after setup, with (current, previous). */
  onConsentChange?: (current: boolean, previous: boolean) => void
  /** Fires once when the OneTrust SDK has initialised. Never fires on exempt locales. */
  onOneTrustLoaded?: () => void
}

/** `",C0001,C0002,"` (the shape of `window.OnetrustActiveGroups`) → `['C0001', 'C0002']` */
const parseActiveGroups = (raw?: string | null): string[] =>
  raw ? raw.split(',').filter(Boolean) : []

/** True when every requested category is in the active groups. */
const hasCategories = (groups: string[], categories: string[]): boolean =>
  categories.every(category => groups.includes(category))

/** Self-stopping watcher.
 * Runs `callback` as soon as `source` is true, now or later, exactly once.
 */
const once = (source: Ref<boolean>, callback: () => void) => {
  if (source.value) return callback()
  const stop = watch(
    source,
    value => {
      if (!value) return
      stop()
      callback()
    },
    { flush: 'sync' }
  )
}

/**
 * Shared OneTrust consent state for consent-gated integrations
 * (Datadog, Inside, Algolia Insights, ...).
 *
 * Source of truth is `window.OnetrustActiveGroups`, read at setup (OneTrust may have
 * initialised before the consumer) and on every `OneTrustGroupsUpdated` event, which
 * OneTrust fires on init and on each consent change. Locales in `notLoadCookieBanner`
 * never load OneTrust and count as consented from the start.
 * Client-only: on the server it reports the exempt state and never fires handlers.
 */
export const useUserCookieConsent = (options: UserCookieConsentOptions) => {
  const { categories, currentCountryLocale } = options
  const locale = currentCountryLocale ?? useCountryLocale().countryLocale.value
  const isConsentExempt = notLoadCookieBanner.includes(locale)

  if (import.meta.server) {
    return {
      hasConsent: computed(() => isConsentExempt),
      isOneTrustLoaded: ref(false),
      isConsentExempt,
      stop: () => {},
    }
  }

  const activeGroups = ref<string[]>([])
  const isOneTrustLoaded = ref(false)
  const hasConsent = computed(
    () => isConsentExempt || hasCategories(activeGroups.value, categories)
  )

  const evaluateConsent = () => {
    const raw = window.OnetrustActiveGroups
    if (raw === undefined) return
    isOneTrustLoaded.value = true
    activeGroups.value = parseActiveGroups(raw)
  }

  window.addEventListener('OneTrustGroupsUpdated', evaluateConsent)
  evaluateConsent()

  // First accept handler
  if (options.onFirstAccept) once(hasConsent, options.onFirstAccept)
  // OneTrust load handler
  if (options.onOneTrustLoaded)
    once(isOneTrustLoaded, options.onOneTrustLoaded)
  // Consent change watcher
  const stopChange = options.onConsentChange
    ? watch(
        hasConsent,
        (current, previous) => options.onConsentChange!(current, previous),
        { flush: 'sync' }
      )
    : undefined

  const stop = () => {
    window.removeEventListener('OneTrustGroupsUpdated', evaluateConsent)
    stopChange?.()
  }

  return { hasConsent, isOneTrustLoaded, isConsentExempt, stop }
}
